import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  GoogleMap,
  useJsApiLoader,
  DirectionsRenderer,
} from "@react-google-maps/api";
import { getStoredData, saveToGlobalState } from "../../utils/localStorage";
import StepFooter from "../../components/step-footer/StepFooter";

const GOOGLE_API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

const containerStyle = { width: "100%", height: "100%" };
const defaultCenter = { lat: -15.7801, lng: -47.9292 };
const mapId = "DEMO_MAP_ID";
const ROUTE_CITIES_STORAGE_KEY = "cidades_trajeto";

const PLACE_FIELDS = [
  "id",
  "displayName",
  "formattedAddress",
  "location",
  "types",
  "addressComponents",
  "shortFormattedAddress",
  "adrFormatAddress",
];

const MONTHS_PT = [
  "Janeiro",
  "Fevereiro",
  "Março",
  "Abril",
  "Maio",
  "Junho",
  "Julho",
  "Agosto",
  "Setembro",
  "Outubro",
  "Novembro",
  "Dezembro",
];

const WEEK_DAYS_PT = ["Seg", "Ter", "Qua", "Qui", "Sex", "Sáb", "Dom"];

const pad2 = (n) => String(n).padStart(2, "0");

function getTodayValue() {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function parseValueToLocalDate(value) {
  if (!value) return null;

  const [year, month, day] = value.split("-").map(Number);

  return new Date(year, month - 1, day);
}

function dateToValue(date) {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");
}

function isBeforeDateValue(a, b) {
  return a < b;
}

function CalendarPicker({
  title,
  selectedValue,
  minValue,
  maxValue,
  onSelect,
}) {
  const initialMonth = useMemo(() => {
    const base = selectedValue || minValue || getTodayValue();
    const date = parseValueToLocalDate(base);
    return new Date(date.getFullYear(), date.getMonth(), 1);
  }, [selectedValue, minValue]);

  const [viewMonth, setViewMonth] = useState(initialMonth);

  useEffect(() => {
    const base = selectedValue || minValue || getTodayValue();
    const date = parseValueToLocalDate(base);
    setViewMonth(new Date(date.getFullYear(), date.getMonth(), 1));
  }, [selectedValue, minValue]);

  const minDateObj = parseValueToLocalDate(minValue);
  const minMonthObj = minDateObj
    ? new Date(minDateObj.getFullYear(), minDateObj.getMonth(), 1)
    : null;

  const prevMonth = new Date(
    viewMonth.getFullYear(),
    viewMonth.getMonth() - 1,
    1,
  );
  const nextMonth = new Date(
    viewMonth.getFullYear(),
    viewMonth.getMonth() + 1,
    1,
  );

  const canGoPrev = !minMonthObj
    ? true
    : viewMonth.getFullYear() > minMonthObj.getFullYear() ||
      (viewMonth.getFullYear() === minMonthObj.getFullYear() &&
        viewMonth.getMonth() > minMonthObj.getMonth());

  const daysInMonth = new Date(
    viewMonth.getFullYear(),
    viewMonth.getMonth() + 1,
    0,
  ).getDate();

  const firstDayOfMonth = new Date(
    viewMonth.getFullYear(),
    viewMonth.getMonth(),
    1,
  );
  const startOffset = (firstDayOfMonth.getDay() + 6) % 7;

  const cells = [];

  for (let i = 0; i < startOffset; i += 1) cells.push(null);

  for (let day = 1; day <= daysInMonth; day += 1) {
    cells.push(new Date(viewMonth.getFullYear(), viewMonth.getMonth(), day));
  }

  while (cells.length % 7 !== 0) cells.push(null);

  const isDisabled = (dateObj) => {
    if (!dateObj) return true;

    const value = dateToValue(dateObj);

    if (minDateObj && isBeforeDateValue(value, dateToValue(minDateObj))) {
      return true;
    }

    if (maxValue) {
      const maxDateObj = parseValueToLocalDate(maxValue);
      if (maxDateObj && isBeforeDateValue(dateToValue(maxDateObj), value)) {
        return true;
      }
    }

    return false;
  };

  return (
    <div className="calendar-card">
      <div className="calendar-header">
        <div>
          <p className="calendar-title">{title}</p>
          <p className="calendar-month">
            {MONTHS_PT[viewMonth.getMonth()]} {viewMonth.getFullYear()}
          </p>
        </div>

        <div className="calendar-nav">
          <button
            type="button"
            className="calendar-nav-btn"
            onClick={() => canGoPrev && setViewMonth(prevMonth)}
            disabled={!canGoPrev}
            aria-label="Mês anterior"
          >
            ‹
          </button>
          <button
            type="button"
            className="calendar-nav-btn"
            onClick={() => setViewMonth(nextMonth)}
            aria-label="Próximo mês"
          >
            ›
          </button>
        </div>
      </div>

      <div className="weekday-row">
        {WEEK_DAYS_PT.map((day) => (
          <div key={day} className="weekday-cell">
            {day}
          </div>
        ))}
      </div>

      <div className="days-grid">
        {cells.map((dateObj, index) => {
          if (!dateObj) {
            return <div key={`empty-${index}`} className="day-cell empty" />;
          }

          const value = dateToValue(dateObj);
          const selected = selectedValue === value;
          const disabled = isDisabled(dateObj);

          return (
            <button
              key={value}
              type="button"
              className={`day-cell ${selected ? "selected" : ""} ${
                disabled ? "disabled" : ""
              }`}
              onClick={() => !disabled && onSelect(value)}
              disabled={disabled}
            >
              {dateObj.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TimeSelect({
  label,
  value,
  onChange,
  options,
  disabled,
  helperText,
  className,
}) {
  return (
    <div className="field-block">
      <select
        className={`time-select ${className || ""}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        style={{
          color: "rgba(63, 67, 68, 0.9)",
          fontSize: 14,
          boxShadow: "none",
          height: 47,
        }}
      >
        <option value="">Selecionar hora</option>
        {options.map((time) => (
          <option key={time} value={time}>
            {time}
          </option>
        ))}
      </select>
      {helperText ? <p className="helper-text">{helperText}</p> : null}
    </div>
  );
}

function dedupeMappedCities(items) {
  const seen = new Set();

  return items.filter((item) => {
    if (!item?.key) return false;
    if (seen.has(item.key)) return false;
    seen.add(item.key);
    return true;
  });
}

function getRawAddressComponents(place) {
  return place?.addressComponents || place?.address_components || [];
}

function getTextValue(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") {
    if (typeof value.text === "string") return value.text;
    if (typeof value.toString === "function") return value.toString();
  }
  if (typeof value.toString === "function") return value.toString();
  return "";
}

function getPlaceName(place) {
  return (
    place?.name ||
    place?.displayName?.text ||
    getTextValue(place?.displayName) ||
    ""
  );
}

function getFormattedAddress(place) {
  return (
    place?.formattedAddress ||
    place?.formatted_address ||
    place?.shortFormattedAddress ||
    ""
  );
}

function normalizeText(text = "") {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function getLocationLiteral(location) {
  if (!location) return null;

  const lat =
    typeof location.lat === "function" ? location.lat() : location.lat;
  const lng =
    typeof location.lng === "function" ? location.lng() : location.lng;

  if (typeof lat !== "number" || typeof lng !== "number") return null;
  return { lat, lng };
}

function getCityFromAddress(place) {
  const components = getRawAddressComponents(place);

  const city =
    components.find((c) => c.types?.includes("locality"))?.longText ||
    components.find((c) => c.types?.includes("locality"))?.long_name ||
    components.find((c) => c.types?.includes("administrative_area_level_2"))
      ?.longText ||
    components.find((c) => c.types?.includes("administrative_area_level_2"))
      ?.long_name ||
    "";

  const state =
    components.find((c) => c.types?.includes("administrative_area_level_1"))
      ?.shortText ||
    components.find((c) => c.types?.includes("administrative_area_level_1"))
      ?.short_name ||
    "";

  const country =
    components.find((c) => c.types?.includes("country"))?.shortText ||
    components.find((c) => c.types?.includes("country"))?.short_name ||
    "";

  if (country && country !== "BR") return null;

  return city || state || null;
}

function getStateFromAddress(place) {
  const components = getRawAddressComponents(place);

  return (
    components.find((c) => c.types?.includes("administrative_area_level_1"))
      ?.shortText ||
    components.find((c) => c.types?.includes("administrative_area_level_1"))
      ?.short_name ||
    ""
  );
}

function getCityFromFormattedAddress(address = "") {
  const parts = String(address)
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);

  for (const part of parts) {
    if (/\s-\s[A-Z]{2}\b/.test(part)) {
      const city = part.split(" - ")[0].trim();
      if (city) return city;
    }
  }

  return null;
}

const getMainCity = (place) => {
  return (
    getCityFromAddress(place) ||
    getCityFromFormattedAddress(getFormattedAddress(place)) ||
    getCityFromFormattedAddress(place?.vicinity || "") ||
    null
  );
};

function isBrazilPlace(place) {
  const address = getFormattedAddress(place).toLowerCase();
  const components = getRawAddressComponents(place);

  const isBR = components.some((c) => {
    const countryType = c.types?.includes("country");
    const short = (c.shortText || c.short_name || "").toUpperCase();
    const long = (c.longText || c.long_name || "").toLowerCase();
    return (
      (countryType && short === "BR") ||
      long.includes("brasil") ||
      long.includes("brazil")
    );
  });

  return address.includes("brasil") || address.includes("brazil") || isBR;
}

function isCityPlace(place) {
  const types = place?.types || [];
  const name = normalizeText(getPlaceName(place));
  const address = normalizeText(getFormattedAddress(place));

  const cityTypes = [
    "locality",
    "postal_town",
    "administrative_area_level_2",
    "administrative_area_level_3",
  ];

  const blocked = [
    "aeroporto",
    "airport",
    "rodoviaria",
    "rodoviária",
    "estacao",
    "estação",
    "station",
    "terminal",
    "shopping",
    "mall",
    "hospital",
    "escola",
    "universidade",
    "faculdade",
    "condominio",
    "edificio",
    "edifício",
    "avenida",
    "rodovia",
    "estrada",
  ];

  if (types.includes("country")) return false;

  if (blocked.some((w) => name.includes(w) || address.includes(w))) {
    return false;
  }

  return types.some((t) => cityTypes.includes(t));
}

function createMarkerContent(label) {
  const el = document.createElement("div");
  el.textContent = label;
  el.style.width = "28px";
  el.style.height = "28px";
  el.style.borderRadius = "999px";
  el.style.display = "flex";
  el.style.alignItems = "center";
  el.style.justifyContent = "center";
  el.style.background = "#1e88e5";
  el.style.color = "#fff";
  el.style.fontSize = "14px";
  el.style.fontWeight = "700";
  el.style.boxShadow = "0 2px 8px rgba(0,0,0,0.25)";
  el.style.border = "2px solid #fff";
  return el;
}

function getCityKeyFromLabel(label = "") {
  return normalizeText(
    String(label || "")
      .split(" - ")[0]
      .trim(),
  );
}

async function geocodeCity(query) {
  const geocoder = new window.google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode(
      {
        address: `${query}, Brasil`,
        componentRestrictions: { country: "BR" },
      },
      (results, status) => {
        if (status === "OK" && results?.length) {
          resolve(results[0]);
        } else {
          resolve(null);
        }
      },
    );
  });
}

async function runTextSearch(query, cityLocation) {
  const { Place } = await window.google.maps.importLibrary("places");

  try {
    const response = await Place.searchByText({
      textQuery: `${query}`,
      language: "pt-BR",
      region: "BR",
      maxResultCount: 20,
      fields: PLACE_FIELDS,
      ...(cityLocation
        ? {
            locationBias: {
              center: cityLocation,
              radius: 50000,
            },
          }
        : {}),
    });

    return response?.places ?? [];
  } catch (error) {
    console.error("Erro searchByText:", error);
    return [];
  }
}

function normalizePlace(place) {
  const p = place;

  return {
    id: p.id || p.resourceName || "",
    place_id: p.id || p.place_id || p.resourceName || "",
    name: getPlaceName(p),
    formattedAddress: getFormattedAddress(p),
    location: p.location || p.geometry?.location || null,
    types: p.types || [],
  };
}

async function searchCities(query) {
  const geocodeResult = await geocodeCity(query);
  const cityLocation = geocodeResult?.geometry?.location || null;

  const variants = [`${query}`, `${query}, Brasil`, `${query} Brasil`];

  const textResultsNested = await Promise.all(
    variants.map((q) => runTextSearch(q, cityLocation)),
  );

  const textResults = textResultsNested.flat();

  const merged = [...(geocodeResult ? [geocodeResult] : []), ...textResults];

  const cleaned = merged.filter(
    (place) => isCityPlace(place) && isBrazilPlace(place),
  );

  const hydrated = await Promise.all(
    cleaned.map(async (place) => {
      try {
        if (typeof place.fetchFields === "function") {
          await place.fetchFields({ fields: PLACE_FIELDS });
        }
        return place;
      } catch (e) {
        console.error("fetchFields falhou:", e);
        return place;
      }
    }),
  );

  const mapped = hydrated
    .map((place) => {
      const p = normalizePlace(place);
      const position = getLocationLiteral(p.location || p.geometry?.location);

      const city =
        getMainCity(place) ||
        getCityFromFormattedAddress(getFormattedAddress(place)) ||
        "";

      const state = getStateFromAddress(place) || "";

      if (!city || !position) return null;

      const label = state ? `${city} - ${state}` : city;

      return {
        rawPlace: place,
        placeId: p.place_id,
        key: normalizeText(`${city}-${state}`),
        city,
        state,
        label,
        title: label,
        subtitle: label,
        position,
        types: p.types,
      };
    })
    .filter(Boolean);

  return dedupeMappedCities(mapped);
}

function brDateToValue(date) {
  if (!date) return "";

  const [dia, mes, ano] = date.split("/");

  return `${ano}-${mes}-${dia}`;
}

function buildStoredStop(stored, prefix) {
  const name = stored[`cidade_${prefix}`] || "";
  const mainCity = stored[`cidade_${prefix}_main`] || "";
  const searchValue = stored[`search_${prefix}`] || "";

  const lat = Number(stored[`lat_cidade_${prefix}`]);
  const lng = Number(stored[`lng_cidade_${prefix}`]);

  const placeId =
    stored[`place_id_${prefix}`] || stored[`station_${prefix}_place_id`] || "";

  if (!name || Number.isNaN(lat) || Number.isNaN(lng)) return null;

  return {
    place_id: placeId,
    name,
    mainCity,
    searchValue,
    position: { lat, lng },
  };
}

function getTodayDate() {
  const now = new Date();
  const tzOffset = now.getTimezoneOffset() * 60000;
  const local = new Date(now.getTime() - tzOffset);
  return local.toISOString().slice(0, 10);
}

function getCurrentTimeHHMM() {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(
    now.getMinutes(),
  ).padStart(2, "0")}`;
}

function parseStoredRouteCities(value) {
  if (!value) return [];

  let parsed = value;

  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      parsed = [];
    }
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((item) => {
      if (typeof item !== "string") return null;

      if (item.includes("#") && item.includes("$")) {
        const [city, rest] = item.split("#");
        const [date, time] = (rest || "").split("$");
        const cleanCity = String(city || "").trim();
        if (!cleanCity) return null;

        return {
          key: normalizeText(cleanCity),
          city: cleanCity,
          label: cleanCity,
          date: date || "",
          time: time || "",
        };
      }

      const cleanCity = item.trim();
      if (!cleanCity) return null;

      return {
        key: normalizeText(cleanCity),
        city: cleanCity,
        label: cleanCity,
        date: "",
        time: "",
      };
    })
    .filter(Boolean);
}

async function getCityFromLatLng(position) {
  if (!position || !window.google) return null;

  const geocoder = new window.google.maps.Geocoder();

  return new Promise((resolve) => {
    geocoder.geocode({ location: position }, (results, status) => {
      if (status === "OK" && results?.length) {
        for (const result of results) {
          const city =
            getCityFromAddress(result) ||
            getCityFromFormattedAddress(result.formatted_address || "") ||
            "";

          const state = getStateFromAddress(result);

          if (city || state) {
            resolve({
              city,
              state,
              label: state ? `${city} - ${state}` : city,
            });
            return;
          }
        }
      }

      resolve(null);
    });
  });
}

async function extractRouteCitiesFromDirections(
  directions,
  originName = "",
  destinationName = "",
) {
  const route = directions?.routes?.[0];
  if (!route?.legs?.length) return [];

  const rawPoints = [];

  for (const leg of route.legs) {
    if (leg?.start_location) rawPoints.push(leg.start_location);
    if (leg?.end_location) rawPoints.push(leg.end_location);

    for (const step of leg.steps || []) {
      if (step?.start_location) rawPoints.push(step.start_location);
      if (step?.end_location) rawPoints.push(step.end_location);
    }
  }

  const uniqueByPos = new Map();

  rawPoints.forEach((p) => {
    const pos = getLocationLiteral(p);
    if (!pos) return;

    const key = `${pos.lat.toFixed(3)}|${pos.lng.toFixed(3)}`;
    if (!uniqueByPos.has(key)) uniqueByPos.set(key, pos);
  });

  let points = Array.from(uniqueByPos.values());

  if (points.length > 30) {
    const sampled = [];
    const step = (points.length - 1) / 29;

    for (let i = 0; i < 30; i++) {
      const idx = Math.round(i * step);
      sampled.push(points[idx]);
    }

    const sampleMap = new Map();
    sampled.forEach((p) => {
      const key = `${p.lat.toFixed(3)}|${p.lng.toFixed(3)}`;
      if (!sampleMap.has(key)) sampleMap.set(key, p);
    });

    points = Array.from(sampleMap.values());
  }

  const excluded = new Set(
    [originName, destinationName].map((v) => normalizeText(String(v || ""))),
  );

  const cities = [];
  const seen = new Set();

  for (const point of points) {
    if (cities.length >= 5) break;

    const info = await getCityFromLatLng(point);
    if (!info) continue;

    const city = String(info.city || "").trim();
    const state = String(info.state || "").trim();
    const label = String(info.label || "").trim();

    if (!city && !state) continue;

    const normalizedKey = normalizeText(city);
    const normalizedLabel = normalizeText(label || city);

    if (excluded.has(normalizedLabel) || excluded.has(normalizedKey)) {
      continue;
    }

    if (seen.has(normalizedKey)) continue;
    seen.add(normalizedKey);

    cities.push({
      key: normalizedKey,
      city,
      state,
      label: label || city,
      lat: point.lat,
      lng: point.lng,
    });
  }

  return cities;
}

function CidadesTrajeto() {
  const navigate = useNavigate();

  const stored = useMemo(() => getStoredData(), []);
  const today = getTodayValue();

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [manualSearch, setManualSearch] = useState("");
  const [manualSuggestions, setManualSuggestions] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);

  const [directions, setDirections] = useState(null);
  const [originPos, setOriginPos] = useState(null);
  const [destinationPos, setDestinationPos] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  const [routeCities, setRouteCities] = useState([]);
  const [selectedRouteCities, setSelectedRouteCities] = useState([]);
  const [loadingRouteCities, setLoadingRouteCities] = useState(false);
  const [hoursRouteSelected, setHoursRouteSelected] = useState(0);
  const [isScheduleMode, setIsScheduleMode] = useState(false);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("manual");
  const [modalCity, setModalCity] = useState(null);
  const [modalDate, setModalDate] = useState("");
  const [modalTime, setModalTime] = useState(getCurrentTimeHHMM());
  const [errorModalDate, setErrorModalDate] = useState("");

  const mapRef = useRef(null);
  const hasRestoredRef = useRef(false);
  const originMarkerRef = useRef(null);
  const destinationMarkerRef = useRef(null);
  const routeCitiesRequestRef = useRef(0);

  const dataOrigemValue = brDateToValue(
    stored.dia_mes_ano_ida_viagem_para_busca,
  );
  const dataDestinoValue = brDateToValue(
    stored.dia_mes_ano_ida_destino_para_busca,
  );

  const getNextAvailableTime = (stepMinutes = 15) => {
    const now = new Date();
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    const nextSlot = Math.ceil(currentMinutes / stepMinutes) * stepMinutes;
    return minutesToTime(nextSlot);
  };

  const generateTimes = (stepMinutes = 15) => {
    const times = [];
    for (let h = 0; h < 24; h += 1) {
      for (let m = 0; m < 60; m += stepMinutes) {
        times.push(`${pad2(h)}:${pad2(m)}`);
      }
    }
    return times;
  };

  const timeToMinutes = (time) => {
    if (!time) return 0;
    const [h, m] = time.split(":").map(Number);
    return h * 60 + m;
  };

  const minutesToTime = (minutes) => {
    const normalized = ((minutes % 1440) + 1440) % 1440;
    const h = Math.floor(normalized / 60);
    const m = normalized % 60;
    return `${pad2(h)}:${pad2(m)}`;
  };

  const allTimes = useMemo(() => generateTimes(15), []);

  const origemTimes = useMemo(() => {
    if (!modalTime) return [];

    return allTimes;
  }, [allTimes, today]);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: GOOGLE_API_KEY,
    language: "pt-BR",
    region: "BR",
  });

  const validarDataHora = () => {
    if (!modalCity?.city) {
      //alert("Selecione uma cidade válida.");
      return;
    }

    if (isScheduleMode && (!modalDate || !modalTime)) {
      //alert("Selecione a data e a hora.");
      return;
    }

    const storedData = getStoredData();

    const cidadeOrigem = storedData?.cidade_origem_main;
    const cidadeDestino = storedData?.cidade_destino_main;

    const dataOrigem = storedData?.dia_mes_ano_ida_viagem_para_busca;
    const dataDestino = storedData?.dia_mes_ano_ida_destino_para_busca;
    const horasIdaViagem = storedData?.horas_ida_viagem;
    const horasIdaDestino = storedData?.horas_ida_destino;

    const [diaOrigem, mesOrigem, anoOrigem] = dataOrigem.split("/");
    const [diaDestino, mesDestino, anoDestino] = dataDestino.split("/");

    const [anoModal, mesModal, diaModal] = modalDate.split("-");

    const dataOrigemObj = new Date(anoOrigem, mesOrigem - 1, diaOrigem);
    const dataDestinoObj = new Date(anoDestino, mesDestino - 1, diaDestino);
    const modalDateObj = new Date(anoModal, mesModal - 1, diaModal);

    if (modalCity.city === cidadeOrigem || modalCity.city === cidadeDestino) {
      setErrorModalDate(
        "Escolha uma cidade diferente da origem e do destino da viagem.",
      );

      setTimeout(() => {
        setErrorModalDate("");
      }, 3000);
      return false;
    }

    /*if (modalDateObj < dataOrigemObj || modalDateObj > dataDestinoObj) {
      setErrorModalDate(
        "A data selecionada deve estar entre a data de partida e a data de chegada.",
      );

      setTimeout(() => {
        setErrorModalDate("");
      }, 3000);
      return false;
    }*/

    const timeToMinutes = (hora) => {
      const [h, m] = hora.split(":").map(Number);
      return h * 60 + m;
    };

    const modalMinutes = timeToMinutes(modalTime);
    const origemMinutes = timeToMinutes(horasIdaViagem);
    const destinoMinutes = timeToMinutes(horasIdaDestino);

    if (modalDateObj.getTime() === dataOrigemObj.getTime()) {
      if (modalMinutes < origemMinutes) {
        setErrorModalDate(
          "Escolha um horário igual ou após ao horário da saída da viagem.",
        );
        setTimeout(() => {
          setErrorModalDate("");
        }, 3000);
        return false;
      }
    }

    if (modalDateObj.getTime() === dataDestinoObj.getTime()) {
      if (modalMinutes < destinoMinutes) {
        setErrorModalDate(
          "Escolha um horário igual ou após o horário da chegada da viagem.",
        );
        setTimeout(() => {
          setErrorModalDate("");
        }, 3000);
        return false;
      }
    }

    return true;
  };

  useEffect(() => {
    if (!isLoaded || !window.google || hasRestoredRef.current) return;

    const stored = getStoredData();

    const restoredOrigin = buildStoredStop(stored, "origem");
    const restoredDestination = buildStoredStop(stored, "destino");

    const hours = Number(stored.horas_rota_selecionada || 0);
    const scheduleMode = hours >= 8;

    setHoursRouteSelected(hours);
    setIsScheduleMode(scheduleMode);

    if (restoredOrigin) setOriginPos(restoredOrigin.position);
    if (restoredDestination) setDestinationPos(restoredDestination.position);

    setSelectedRouteCities(parseStoredRouteCities(stored.cidades_trajeto));

    if (restoredDestination?.position) {
      setMapCenter(restoredDestination.position);
    } else if (restoredOrigin?.position) {
      setMapCenter(restoredOrigin.position);
    }

    hasRestoredRef.current = true;
  }, [isLoaded]);

  useEffect(() => {
    if (!originPos || !destinationPos || !window.google) return;

    const DirectionsService = new window.google.maps.DirectionsService();

    DirectionsService.route(
      {
        origin: originPos,
        destination: destinationPos,
        travelMode: window.google.maps.TravelMode.DRIVING,
      },
      (result, status) => {
        if (status === "OK") {
          setDirections(result);
        } else {
          console.error("Erro ao gerar rota:", status);
          setDirections(null);
          setRouteCities([]);
        }
      },
    );
  }, [originPos, destinationPos]);

  useEffect(() => {
    let cancelled = false;

    async function loadRouteCities() {
      if (!isLoaded || !directions) {
        setRouteCities([]);
        return;
      }

      const requestId = ++routeCitiesRequestRef.current;
      setLoadingRouteCities(true);

      try {
        const stored = getStoredData();
        const originName =
          stored.cidade_origem_main || stored.cidade_origem || "";
        const destinationName =
          stored.cidade_destino_main || stored.cidade_destino || "";

        const cities = await extractRouteCitiesFromDirections(
          directions,
          originName,
          destinationName,
        );

        if (cancelled || requestId !== routeCitiesRequestRef.current) return;
        setRouteCities(cities);
      } catch (error) {
        if (!cancelled) setRouteCities([]);
      } finally {
        if (!cancelled) setLoadingRouteCities(false);
      }
    }

    loadRouteCities();

    return () => {
      cancelled = true;
    };
  }, [directions, isLoaded]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    let cancelled = false;

    (async () => {
      const { AdvancedMarkerElement } =
        await window.google.maps.importLibrary("marker");

      if (cancelled) return;

      if (originMarkerRef.current) {
        originMarkerRef.current.map = null;
        originMarkerRef.current = null;
      }

      if (destinationMarkerRef.current) {
        destinationMarkerRef.current.map = null;
        destinationMarkerRef.current = null;
      }

      const map = mapRef.current;

      if (originPos) {
        originMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: originPos,
          title: "Origem",
          content: createMarkerContent("A"),
        });
      }

      if (destinationPos) {
        destinationMarkerRef.current = new AdvancedMarkerElement({
          map,
          position: destinationPos,
          title: "Destino",
          content: createMarkerContent("B"),
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isLoaded, originPos, destinationPos]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google) return;

    const raf = requestAnimationFrame(() => {
      const map = mapRef.current;
      if (!map) return;

      if (originPos && destinationPos) {
        const bounds = new window.google.maps.LatLngBounds();
        bounds.extend(originPos);
        bounds.extend(destinationPos);

        map.fitBounds(bounds, {
          top: 80,
          bottom: 80,
          left: 80,
          right: 80,
        });
        return;
      }

      if (originPos) {
        map.setCenter(originPos);
        map.setZoom(14);
        return;
      }

      if (destinationPos) {
        map.setCenter(destinationPos);
        map.setZoom(14);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [isLoaded, originPos, destinationPos]);

  useEffect(() => {
    if (!modalOpen || modalMode !== "manual" || !isLoaded) return;

    const query = manualSearch.trim();

    if (query.length < 2) {
      setManualSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setManualLoading(true);

        const results = await searchCities(query);

        const filtered = results.filter(
          (item) => item.city && item.label && item.position,
        );

        setManualSuggestions(dedupeMappedCities(filtered).slice(0, 8));
      } catch (error) {
        console.error("Erro ao buscar cidades:", error);
        setManualSuggestions([]);
      } finally {
        setManualLoading(false);
      }
    }, 350);

    return () => clearTimeout(timer);
  }, [manualSearch, modalOpen, modalMode, isLoaded]);

  const encodeRouteCities = (items) => {
    if (!isScheduleMode) {
      return items.map((item) => item.city);
    }

    return items.map((item) => `${item.city}#${item.date}$${item.time}`);
  };

  const persistRouteCities = (items) => {
    setSelectedRouteCities(items);

    const encoded = encodeRouteCities(items);

    saveToGlobalState({
      [ROUTE_CITIES_STORAGE_KEY]: encoded,
    });
  };

  const addToTop = (cityItem) => {
    const item = {
      key: normalizeText(cityItem.city || cityItem.label || ""),
      city: cityItem.city || cityItem.label || "",
      label: cityItem.label || cityItem.city || "",
      state: cityItem.state || "",
      date: isScheduleMode ? modalDate : "",
      time: isScheduleMode ? modalTime : "",
    };

    const next = [
      item,
      ...selectedRouteCities.filter((prev) => prev.key !== item.key),
    ];

    persistRouteCities(next);

    setRouteCities((prev) => {
      if (prev.some((x) => x.key === item.key)) return prev;
      return [cityItem, ...prev];
    });
  };

  const removeFromSelected = (cityItem) => {
    const next = selectedRouteCities.filter(
      (item) => item.key !== cityItem.key,
    );
    persistRouteCities(next);
  };

  const openRouteCityModal = (cityItem) => {
    setModalMode("route");
    setModalCity(cityItem);

    const existing = selectedRouteCities.find(
      (item) => item.key === cityItem.key,
    );
    setModalDate(existing?.date || "");
    setModalTime(existing?.time || getCurrentTimeHHMM());
    setModalOpen(true);
  };

  const openManualModal = () => {
    setModalMode("manual");
    setModalCity(null);
    setManualSearch("");
    setManualSuggestions([]);
    setModalDate("");
    setModalTime(getCurrentTimeHHMM());
    setModalOpen(true);
  };

  const handleRouteCardClick = (cityItem) => {
    const exists = selectedRouteCities.some(
      (item) => item.key === cityItem.key,
    );

    if (exists) {
      removeFromSelected(cityItem);
      return;
    }

    if (isScheduleMode) {
      openRouteCityModal(cityItem);
      return;
    }

    addToTop(cityItem);
  };

  const handleConfirmModal = () => {
    if (!validarDataHora()) return;

    const nextItem = {
      key: normalizeText(modalCity.city),
      city: modalCity.city,
      label: modalCity.label || modalCity.city,
      state: modalCity.state || "",
      date: isScheduleMode ? modalDate : "",
      time: isScheduleMode ? modalTime : "",
    };

    const nextSelected = [
      nextItem,
      ...selectedRouteCities.filter((item) => item.key !== nextItem.key),
    ];

    persistRouteCities(nextSelected);

    setRouteCities((prev) => {
      if (prev.some((x) => x.key === nextItem.key)) return prev;
      return [
        {
          key: nextItem.key,
          city: nextItem.city,
          state: nextItem.state,
          label: nextItem.label,
          title: nextItem.label,
          position: modalCity.position || null,
          subtitle: modalCity.state
            ? `${modalCity.city} - ${modalCity.state}`
            : modalCity.city,
        },
        ...prev,
      ];
    });

    setModalOpen(false);
    setModalCity(null);
  };

  const handleNext = () => {
    if (!selectedRouteCities.length) {
      saveToGlobalState({
        [ROUTE_CITIES_STORAGE_KEY]: [],
      });
    }

    navigate("/espaco-disponivel");
  };

  const handleBack = () => {
    navigate("/data");
  };

  const selectedKeys = useMemo(
    () => new Set(selectedRouteCities.map((item) => item.key)),
    [selectedRouteCities],
  );

  const selectedDisplayCities = useMemo(() => {
    const fromSelected = selectedRouteCities.map((item) => ({
      key: item.key,
      city: item.city,
      label: item.label || item.city,
      state: item.state || "",
      date: item.date || "",
      time: item.time || "",
      selected: true,
    }));

    return fromSelected;
  }, [selectedRouteCities]);

  const availableDisplayCities = useMemo(() => {
    return routeCities
      .filter((item) => !selectedKeys.has(item.key))
      .map((item) => ({
        key: item.key,
        city: item.city,
        label: item.label || item.city,
        state: item.state || "",
        selected: false,
      }))
      .slice(0, 5);
  }, [routeCities, selectedKeys]);

  const renderMeta = (city) => {
    if (isScheduleMode) {
      if (city.selected && city.date && city.time) {
        return `${city.date} • ${city.time}`;
      }
      return city.selected
        ? "Toque para desmarcar"
        : "Toque para definir data e hora";
    }

    return city.selected ? "Toque para desmarcar" : "Toque para selecionar";
  };

  const shouldShowSuggestions =
    modalMode === "manual" && !modalCity && manualSearch.trim().length >= 2;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key !== "Enter") return;

      if (modalOpen) return;

      e.preventDefault();
      handleNext();
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalOpen, handleNext]);

  return (
    <>
      <style>{`
        :root {
          --primary: #0a84ff;
          --primary-dark: #0066cc;
          --bg: #eef4fb;
          --white: #ffffff;
          --border: rgba(148, 163, 184, 0.22);
          --text: #0f172a;
          --text-light: #64748b;
          --accent: #7c3aed;
        }

        html, body, #root {
          margin: 0;
          padding: 0;
          height: 100%;
          overflow: hidden;
        }

        * {
          box-sizing: border-box;
        }

        .main-container {
          display: flex;
          gap: 16px;
          height: calc(96vh - 120px);
          overflow: hidden;
          padding: 16px;
          background: transparent;
        }

        .content-side {
          width: 480px;
          min-width: 480px;
          padding: 22px;
          display: flex;
          flex-direction: column;
          border-radius: 28px;
          backdrop-filter: blur(18px);
          -webkit-backdrop-filter: blur(18px);
          border: 1px solid rgba(255, 255, 255, 0.7);
          overflow: hidden;
          position: relative;
        }

        .content-side h1 {
          margin: 0 0 8px 0;
          font-size: 1.35rem;
          line-height: 1.15;
          font-weight: 800;
          color: var(--text);
          letter-spacing: -0.03em;
          position: relative;
          z-index: 1;
        }

        .toolbar {
          display: flex;
          gap: 10px;
          margin-top: 14px;
          position: relative;
          z-index: 1;
        }

        .btn-add-city {
          width: 100%;
          padding: 14px 16px;
          border-radius: 16px;
          border: none;
          background: linear-gradient(135deg, #0a84ff, #0066cc);
          color: #fff;
          font-size: 0.95rem;
          font-weight: 800;
          cursor: pointer;
          box-shadow: 0 14px 26px rgba(10, 132, 255, 0.22);
          transition: transform 0.18s ease, box-shadow 0.18s ease;
        }

        .btn-add-city:hover {
          background: #0ed079ff 100%;
        }

        .sections {
          flex: 1;
          margin-top: 16px;
          overflow-y: auto;
          padding-right: 6px;
          position: relative;
          z-index: 1;
        }

        .sections::-webkit-scrollbar {
          width: 10px;
        }

        .sections::-webkit-scrollbar-thumb {
          background: rgba(100, 116, 139, 0.26);
          border-radius: 999px;
        }

        .section-title {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 10px 0 10px;
          font-size: 0.86rem;
          font-weight: 800;
          letter-spacing: 0.03em;
          text-transform: uppercase;
          color: var(--text-light);
        }

        .city-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
          padding: 5px;
        }

        .city-card {
          width: 100%;
          text-align: left;
          border: 1px solid var(--border);
          background: rgba(255, 255, 255, 0.92);
          padding: 16px 16px;
          border-radius: 18px;
          cursor: pointer;
          transition:
            transform 0.18s ease,
            box-shadow 0.18s ease,
            border-color 0.18s ease,
            background 0.18s ease;
          box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.1);
          display: flex;
          gap: 14px;
          align-items: center;
          position: relative;
          overflow: hidden;
        }

        .city-card::before {
          content: "";
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: linear-gradient(180deg, var(--primary), var(--accent));
          opacity: 0;
          transition: opacity 0.18s ease;
        }

        .city-card:hover {
          border-color: rgba(10, 132, 255, 0.30);
          box-shadow: 0 14px 30px rgba(15, 23, 42, 0.08);
        }

        .city-card:hover::before {
          opacity: 1;
        }

        .city-card.selected {
          background: linear-gradient(135deg, rgba(10,132,255,0.10), rgba(124,58,237,0.08));
          border-color: rgba(10, 132, 255, 0.52);
          box-shadow: 0 18px 34px rgba(10, 132, 255, 0.12);
          transform: translateY(-1px);
        }

        .city-card.selected::before {
          opacity: 1;
        }

        .city-pill {
          width: 18px;
          height: 18px;
          border-radius: 999px;
          flex-shrink: 0;
          border: 2px solid rgba(10, 132, 255, 0.35);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: #fff;
          margin-top: 1px;
        }

        .city-card.selected .city-pill {
          background: var(--primary);
          border-color: var(--primary);
        }

        .city-pill::after {
          content: "";
          width: 8px;
          height: 8px;
          border-radius: 999px;
          background: transparent;
        }

        .city-card.selected .city-pill::after {
          background: white;
        }

        .city-text {
          display: flex;
          flex-direction: column;
          gap: 4px;
          min-width: 0;
          flex: 1;
        }

        .city-text strong {
          font-size: 0.97rem;
          color: var(--text);
          font-weight: 800;
          line-height: 1.2;
        }

        .city-meta {
          font-size: 0.82rem;
          color: var(--text-light);
          font-weight: 600;
        }

        .actions-row {
          display: flex;
          gap: 12px;
          padding-top: 16px;
          margin-top: auto;
          position: relative;
          z-index: 1;
        }

        .btn-next {
            flex: 1;
            padding: 14px 16px;
            border: none;
            color: #fff;
            cursor: pointer;
            transition:
              transform 0.18s ease,
              box-shadow 0.18s ease,
              filter 0.18s ease;
          }

        .btn-back {
          flex: 1;
          padding: 14px 16px;
          border: 1px solid rgba(148, 163, 184, 0.28);
          color: var(--text);
          cursor: pointer;
          transition:
            transform 0.18s ease,
            box-shadow 0.18s ease,
            background 0.18s ease,
            border-color 0.18s ease;
        }

        .map-side {
          flex: 1;
          position: relative;
          min-width: 0;
          border-radius: 28px;
          overflow: hidden;
          background: #dfe9f5;
          border: 1px solid rgba(255, 255, 255, 0.7);
        }

        .map-side > div {
          width: 100%;
          height: 100%;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(15, 23, 42, 0.55);
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 9999;
          padding: 16px;
        }

        .modal-card {
          width: min(460px, 100%);
          height: auto;
          border-radius: 24px;
          background: rgba(255, 255, 255, 0.96);
          box-shadow: 0 24px 80px rgba(15, 23, 42, 0.22);
          border: 1px solid rgba(255, 255, 255, 0.9);
          padding: 20px;
          position: relative;
          overflow: hidden;
        }

        .modal-card::before {
          content: "";
          position: absolute;
          inset: -80px auto auto -80px;
          width: 180px;
          height: 180px;
          background: radial-gradient(circle, rgba(10,132,255,0.18), transparent 70%);
          pointer-events: none;
        }

        .modal-title {
          margin: 0 0 6px 0;
          font-size: 1.15rem;
          color: var(--text);
          font-weight: 800;
          letter-spacing: -0.02em;
        }

        .modal-subtitle {
          margin: 0 0 18px 0;
          color: var(--text-light);
          font-size: 0.95rem;
        }

        .modal-field {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-bottom: 14px;
        }

        .modal-field label {
          font-size: 0.88rem;
          color: var(--text);
          font-weight: 700;
        }

        .modal-field input {
          width: 100%;
          padding: 14px 14px;
          border-radius: 14px;
          border: 1px solid rgba(148, 163, 184, 0.3);
          outline: none;
          font-size: 0.95rem;
          background: #fff;
          transition: border-color 0.2s ease, box-shadow 0.2s ease;
        }

        .modal-field input:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 4px rgba(10,132,255,0.14);
        }

        .autocomplete-box {
          min-height: 52px;
          border-radius: 14px;
          border: 1px solid rgba(148, 163, 184, 0.3);
          background: #fff;
          padding: 10px;
        }

        .autocomplete-box button {
          background: transparent
        }

        .autocomplete-box button:hover {
          background: #f5f7fa
        }

        .modal-city-chip {
          margin-top: 10px;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 12px;
          border-radius: 999px;
          background: rgba(10, 132, 255, 0.08);
          color: var(--text);
          font-weight: 700;
          font-size: 0.92rem;
        }

        .modal-actions {
          display: flex;
          gap: 10px;
          margin-top: 16px;
        }

        .modal-btn {
          flex: 1;
          padding: 10px 12px;
          border-radius: 14px;
          border: none;
          font-size: 0.95rem;
          font-weight: 800;
          cursor: pointer;
          background: #fff;
          transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
        }

        .modal-btn:hover { 
          background: #e2e1e1
        }

        .modal-btn-primary {
          background: linear-gradient(90deg, #1a7fe7 0%, #19c4c9 40%);
          color: #fff;
        }

        .modal-btn-primary:hover {
          background: #0ed079ff 100%;
        } 

        .modal-btn-secondary {
          background: #fff;
          color: var(--text);
        }

       @media (max-width: 934px) {
          html,
          body,
          #root {
            height: auto;
            min-height: 100%;
            overflow-y: auto !important;
            overflow-x: hidden !important;
          }

          .main-container {
            display: flex;
            flex-direction: column;
            height: auto;
            min-height: auto;
            overflow: visible;
            margin-top: 0px;
            padding: 12px;
            gap: 12px;
          }

          .content-side {
            display: contents;
          }

          .content-side > h1 {
            order: 1;
            font-size: 0.9rem;
          }

          .btn-add-city {
            font-size: 0.8rem;
          }

          .content-side > .toolbar {
            order: 2;
            margin: 0 12px 12px;
          }

          .content-side > .sections {
            order: 3;

            max-height: 320px;
            overflow-y: auto;

            padding-right: 4px;

            -webkit-overflow-scrolling: touch;
          }

          .content-side > .sections::-webkit-scrollbar {
            width: 6px;
          }

          .content-side > .sections::-webkit-scrollbar-thumb {
            background: rgba(100, 116, 139, 0.35);
            border-radius: 999px;
          }

          .map-side {
            order: 4;
            width: calc(100% - 24px);
            margin: 0 12px;
            height: 400px;
            min-height: 400px;
            border-radius: 24px;
            align-self: stretch;
            position: relative;
            overflow: hidden;
          }

          .map-container {
            width: 100% !important;
            height: 100% !important;
            min-height: 400px !important;
          }

          .content-side > .actions-row {
            order: 5;
            margin: 0 12px;
            padding-top: 12px;
            flex-direction: row;
          }
        }

        @media (min-width: 935px) { 
          .content-side::after {
            content: "";
            position: absolute;
            inset: auto auto -120px -80px;
            width: 240px;
            height: 240px;
            pointer-events: none;
          }

          .content-side::before {
            content: "";
            position: absolute;
            inset: -120px -60px auto auto;
            width: 220px;
            height: 220px;
            pointer-events: none;
          }

          .map-container {
            height: 400px; 
          }
        }
      `}</style>

      <div className="main-container">
        <div className="content-side">
          <h1>CIDADES NO TRAJETO</h1>

          <div className="toolbar">
            <button
              className="btn-add-city"
              type="button"
              onClick={openManualModal}
            >
              + Adicionar cidade manualmente
            </button>
          </div>

          <div className="sections">
            <div className="section-title">
              <span>Selecionadas</span>
              <span>{selectedDisplayCities.length}</span>
            </div>

            <div className="city-list">
              {selectedDisplayCities.length === 0 && (
                <p style={{ color: "#64748b", marginTop: 0 }}>
                  Nenhuma cidade selecionada ainda.
                </p>
              )}

              {selectedDisplayCities.map((city) => (
                <button
                  key={city.key}
                  type="button"
                  className="city-card selected"
                  onClick={() => removeFromSelected(city)}
                >
                  <span className="city-pill" />
                  <span className="city-text">
                    <strong>{city.label}</strong>
                    <span className="city-meta">{renderMeta(city)}</span>
                  </span>
                </button>
              ))}
            </div>

            <div className="section-title" style={{ marginTop: 18 }}>
              <span>Disponíveis</span>
              <span>{availableDisplayCities.length}</span>
            </div>

            <div className="city-list">
              {loadingRouteCities && <p>Carregando cidades do trajeto...</p>}

              {!loadingRouteCities && availableDisplayCities.length === 0 && (
                <p style={{ color: "#64748b", marginTop: 0 }}>
                  Nenhuma cidade disponível na rota.
                </p>
              )}

              {availableDisplayCities.map((cityItem) => (
                <button
                  key={cityItem.key}
                  type="button"
                  className="city-card"
                  onClick={() => handleRouteCardClick(cityItem)}
                >
                  <span className="city-pill" />
                  <span className="city-text">
                    <strong>{cityItem.label}</strong>
                    <span className="city-meta">{renderMeta(cityItem)}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>

          <div className="actions-row">
            <button className="btn-back" onClick={handleBack} type="button">
              Voltar
            </button>

            <button className="btn-next" onClick={handleNext} type="button">
              Próximo
            </button>
          </div>
        </div>

        <div className="map-side">
          {isLoaded && (
            <GoogleMap
              mapContainerClassName="map-container"
              center={mapCenter}
              zoom={10}
              options={{ mapId }}
              onLoad={(map) => {
                mapRef.current = map;

                if (originPos && destinationPos) {
                  const bounds = new window.google.maps.LatLngBounds();
                  bounds.extend(originPos);
                  bounds.extend(destinationPos);
                  map.fitBounds(bounds);
                  return;
                }

                if (originPos) {
                  map.setCenter(originPos);
                  map.setZoom(14);
                  return;
                }

                if (destinationPos) {
                  map.setCenter(destinationPos);
                  map.setZoom(14);
                }
              }}
            >
              {directions && (
                <DirectionsRenderer
                  directions={directions}
                  options={{
                    suppressMarkers: true,
                    polylineOptions: {
                      strokeColor: "#1e88e5",
                      strokeWeight: 4,
                      strokeOpacity: 0.8,
                    },
                  }}
                />
              )}
            </GoogleMap>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              {modalMode === "manual"
                ? "Adicionar cidade manualmente"
                : "Definir cidade do trajeto"}
            </h2>

            <p className="modal-subtitle">
              {modalMode === "manual"
                ? "Pesquise uma cidade pelo Google Maps e depois informe a data e hora."
                : "Informe a data e a hora em que você passa por essa cidade."}
            </p>

            {errorModalDate && (
              <div
                style={{
                  backgroundColor: "#fde8e8",
                  color: "#c81e1e",
                  padding: "12px 16px",
                  borderRadius: "8px",
                  border: "1px solid #f8b4b4",
                  fontSize: "14px",
                  marginTop: "12px",
                  marginBottom: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  fontWeight: "500",
                  boxShadow: "0 2px 4px rgba(0,0,0,0.05)",
                  width: "95%",
                  marginLeft: "auto",
                  marginRight: "auto",
                }}
              >
                <span
                  style={{
                    marginRight: "10px",
                    display: "flex",
                    alignItems: "center",
                    color: "#e02424",
                  }}
                ></span>
                {errorModalDate}
              </div>
            )}

            {modalMode === "manual" && (
              <div className="modal-field">
                <label>Pesquisar cidade</label>

                <input
                  type="text"
                  placeholder="Digite o nome da cidade"
                  value={manualSearch}
                  onChange={(e) => {
                    setManualSearch(e.target.value);
                    setModalCity(null);
                  }}
                />

                {shouldShowSuggestions && (
                  <div className="autocomplete-box" style={{ padding: 8 }}>
                    {manualLoading && (
                      <div style={{ padding: "8px 6px", color: "#64748b" }}>
                        Buscando cidades...
                      </div>
                    )}

                    {!manualLoading && manualSuggestions.length === 0 && (
                      <div style={{ padding: "8px 6px", color: "#64748b" }}>
                        Nenhuma cidade encontrada.
                      </div>
                    )}

                    {!manualLoading &&
                      manualSuggestions.map((item) => (
                        <button
                          key={`${item.key}-${item.placeId}`}
                          type="button"
                          onClick={() => {
                            setModalCity({
                              key: item.key,
                              city: item.city,
                              state: item.state || "",
                              label: item.label,
                              position: item.position,
                              place_id: item.placeId || "",
                            });

                            setManualSearch(item.label);
                            setManualSuggestions([]);
                          }}
                          style={{
                            width: "100%",
                            textAlign: "left",
                            padding: "10px 12px",
                            border: "none",
                            borderRadius: 10,
                            cursor: "pointer",
                            marginBottom: 4,
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 700,
                              color: "#0f172a",
                            }}
                          >
                            {item.label}
                          </div>
                        </button>
                      ))}
                  </div>
                )}
              </div>
            )}

            {modalMode === "route" && modalCity?.label && (
              <div className="modal-city-chip" style={{ marginBottom: 14 }}>
                {modalCity.label}
              </div>
            )}

            {isScheduleMode && (
              <>
                <div className="modal-field">
                  <label>Data</label>

                  <div
                    onClick={() => setCalendarOpen(true)}
                    style={{
                      padding: "14px",
                      color: "rgb(63 67 68 / 90%)",
                      fontSize: 14,
                      border: "1px solid rgba(148, 163, 184, 0.3)",
                      borderRadius: "14px",
                      cursor: "default",
                      background: "#fff",
                      minHeight: "50px",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {modalDate
                      ? modalDate.split("-").reverse().join("/")
                      : "Selecione data"}
                  </div>
                </div>

                <div className="modal-field">
                  <label>Hora aproximada</label>
                  {/* <input
                    type="time"
                    value={modalTime}
                    onChange={(e) => setModalTime(e.target.value)}
                  />*/}
                  <TimeSelect
                    label="Hora de Ida"
                    value={modalTime}
                    onChange={(value) => {
                      setModalTime(value);
                    }}
                    options={origemTimes}
                    /* helperText={
                        origemData && origemTimes.length === 0
                          ? "Não há horas disponíveis para esta data."
                          : ""
                      }*/
                    // className={blinkOrigem ? "blink-shadow" : ""}
                  />
                </div>
              </>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="modal-btn modal-btn-secondary"
                onClick={() => setModalOpen(false)}
              >
                Cancelar
              </button>

              <button
                type="button"
                className="modal-btn modal-btn-primary"
                onClick={handleConfirmModal}
              >
                Salvar
              </button>
            </div>
          </div>
        </div>
      )}
      <br />
      {calendarOpen && (
        <div className="modal-overlay" onClick={() => setCalendarOpen(false)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()}>
            <CalendarPicker
              title="Selecione a data"
              selectedValue={modalDate}
              minValue={dataOrigemValue}
              maxValue={dataDestinoValue}
              onSelect={(value) => {
                setModalDate(value);
                setCalendarOpen(false);
              }}
            />
          </div>
        </div>
      )}
      <StepFooter />
    </>
  );
}

export default CidadesTrajeto;
